import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Col,
  Container,
  Form,
  Row,
  Spinner,
  Table,
} from 'react-bootstrap';
import api, { getApiErrorMessage } from '../../services/api';

function downloadBase64Pdf(base64, filename) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  const blob = new Blob([bytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'label.pdf';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function VariantTraits({ color, size }) {
  const parts = [];
  if (color) {
    parts.push(
      <mark key="c" className="bcmr-trait bcmr-trait--color">
        {color}
      </mark>,
    );
  }
  if (size) {
    if (parts.length) parts.push(<span key="sep"> / </span>);
    parts.push(
      <mark key="s" className="bcmr-trait bcmr-trait--size">
        {size}
      </mark>,
    );
  }
  if (!parts.length) return <span className="text-muted">—</span>;
  return <span className="bcmr-traits">{parts}</span>;
}

function containerKey(c) {
  return `${c.container_type || ''}:${c.container_id || ''}`;
}

function ExpandedRowPanel({
  secret,
  row,
  containersState,
  onReloadContainers,
  onFixed,
}) {
  const [selectedKey, setSelectedKey] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [barcode, setBarcode] = useState('');
  const [fixing, setFixing] = useState(false);
  const [localError, setLocalError] = useState('');
  const [localOk, setLocalOk] = useState('');

  const containers = containersState?.containers || [];
  const loading = containersState?.loading;
  const loadError = containersState?.error;

  useEffect(() => {
    if (!selectedKey && containers.length === 1) {
      setSelectedKey(containerKey(containers[0]));
    }
  }, [containers, selectedKey]);

  useEffect(() => {
    const opts = row.variant_options || [];
    if (!barcode && opts.length === 1) {
      setBarcode(opts[0].barcode);
    }
  }, [row.variant_options, barcode]);

  const selected = containers.find((c) => containerKey(c) === selectedKey);

  const handleFix = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setLocalError('');
    setLocalOk('');
    if (!selected) {
      setLocalError('Выберите контейнер (ячейку)');
      return;
    }
    if (!barcode) {
      setLocalError('Выберите баркод для исправления');
      return;
    }
    const qty = Number(quantity);
    if (!Number.isFinite(qty) || qty < 1) {
      setLocalError('Количество должно быть ≥ 1');
      return;
    }

    setFixing(true);
    try {
      const res = await api.postBarcodeMismatchFix(secret, row.id, {
        container_type: selected.container_type,
        container_id: selected.container_id,
        quantity: qty,
        barcode,
      });
      const data = res?.data || {};
      setLocalOk(
        `Готово: списание ${data.loss_name || data.loss_id || 'ok'}, `
          + `приёмка ${data.enter_id || 'ok'}, ячейка обновлена `
          + `(${data.wrong_product_id} → ${data.correct_product_id}).`,
      );
      onFixed?.(data);
      onReloadContainers?.();
    } catch (err) {
      setLocalError(getApiErrorMessage(err, 'Не удалось выполнить исправление'));
    } finally {
      setFixing(false);
    }
  };

  return (
    <div className="bcmr-expand p-3" onClick={(e) => e.stopPropagation()}>
      <div className="fw-semibold mb-2">Расположение на складе (WH)</div>
      {loading ? (
        <div className="py-2">
          <Spinner animation="border" size="sm" className="me-2" />
          Загрузка контейнеров…
        </div>
      ) : null}
      {loadError ? <Alert variant="danger" className="py-2">{loadError}</Alert> : null}
      {!loading && !loadError && containers.length === 0 ? (
        <Alert variant="secondary" className="py-2 mb-3">
          Контейнеры не найдены для этого товара.
        </Alert>
      ) : null}

      {containers.length > 0 ? (
        <Table size="sm" bordered className="mb-3 bcmr-containers-table">
          <thead>
            <tr>
              <th style={{ width: 40 }} />
              <th>Адрес ячейки</th>
              <th>Количество</th>
              <th>Зарезервировано</th>
              <th>Доступно</th>
            </tr>
          </thead>
          <tbody>
            {containers.map((c) => {
              const key = containerKey(c);
              return (
                <tr
                  key={key}
                  className={selectedKey === key ? 'table-primary' : ''}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSelectedKey(key)}
                >
                  <td className="text-center">
                    <Form.Check
                      type="radio"
                      name={`bcmr-cont-${row.id}`}
                      checked={selectedKey === key}
                      onChange={() => setSelectedKey(key)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </td>
                  <td>
                    <code>{c.address || c.name || '—'}</code>
                    <div className="small text-muted">{c.container_type}</div>
                  </td>
                  <td>{c.quantity}</td>
                  <td>{c.reserved}</td>
                  <td>{c.available}</td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      ) : null}

      <Form onSubmit={handleFix}>
        <div className="fw-semibold mb-2">Исправление ошибки в ячейке</div>
        <Row className="g-2 align-items-end">
          <Col md={3} sm={6}>
            <Form.Group>
              <Form.Label className="small mb-1">Количество</Form.Label>
              <Form.Control
                type="number"
                min={1}
                step={1}
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={5} sm={6}>
            <Form.Group>
              <Form.Label className="small mb-1">Баркод (правильный)</Form.Label>
              <Form.Select
                value={barcode}
                onChange={(e) => setBarcode(e.target.value)}
              >
                <option value="">— выберите —</option>
                {(row.variant_options || []).map((opt) => (
                  <option key={opt.barcode} value={opt.barcode}>
                    {opt.barcode}
                    {opt.color || opt.size
                      ? ` (${[opt.color, opt.size].filter(Boolean).join(' / ')})`
                      : ''}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={4} sm={12}>
            <Button
              type="submit"
              variant="danger"
              disabled={fixing || !selectedKey || !barcode}
              className="w-100"
            >
              {fixing ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Выполняется…
                </>
              ) : (
                'Списать → принять → переместить'
              )}
            </Button>
          </Col>
        </Row>
        <div className="small text-muted mt-2">
          Действия: списание неверного товара → приёмка по выбранному ШК → удаление из ячейки →
          размещение нового ШК в ту же ячейку.
        </div>
      </Form>

      {localError ? <Alert variant="danger" className="mt-2 py-2 mb-0">{localError}</Alert> : null}
      {localOk ? <Alert variant="success" className="mt-2 py-2 mb-0">{localOk}</Alert> : null}
    </div>
  );
}

/**
 * Секретная страница сверки баркодов (URL /q/<token>/).
 * Не в меню — только по прямой ссылке.
 */
export default function BarcodeMismatchReviewPage({ secret }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [togglingId, setTogglingId] = useState(null);
  const [printingKey, setPrintingKey] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [containersById, setContainersById] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.getBarcodeMismatchReview(secret);
      setItems(res?.data?.items || []);
    } catch (err) {
      const msg = getApiErrorMessage(err, 'Не удалось загрузить данные');
      const isJwt =
        /token not valid|token_not_valid|authentication|credentials|не авторизован/i.test(
          String(msg),
        ) || err?.status === 401;
      setError(
        isJwt
          ? `${msg} — сессия устарела. Выйдите и войдите снова (F5 не помогает: токен в браузере тот же).`
          : msg,
      );
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [secret]);

  useEffect(() => {
    load();
  }, [load]);

  const loadContainers = useCallback(
    async (rowId) => {
      setContainersById((prev) => ({
        ...prev,
        [rowId]: { ...(prev[rowId] || {}), loading: true, error: '' },
      }));
      try {
        const res = await api.getBarcodeMismatchContainers(secret, rowId);
        setContainersById((prev) => ({
          ...prev,
          [rowId]: {
            loading: false,
            error: '',
            containers: res?.data?.containers || [],
            warehouse_id: res?.data?.warehouse_id,
          },
        }));
      } catch (err) {
        setContainersById((prev) => ({
          ...prev,
          [rowId]: {
            loading: false,
            error: getApiErrorMessage(err, 'Не удалось загрузить контейнеры WH'),
            containers: [],
          },
        }));
      }
    },
    [secret],
  );

  const handleRowClick = (row) => {
    if (expandedId === row.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(row.id);
    const cached = containersById[row.id];
    if (!cached || cached.error || !(cached.containers || []).length) {
      loadContainers(row.id);
    }
  };

  const handleToggle = async (row, e) => {
    e?.stopPropagation?.();
    setTogglingId(row.id);
    setError('');
    try {
      const res = await api.toggleBarcodeMismatchReview(secret, row.id);
      const updated = res?.data;
      if (updated?.id) {
        setItems((prev) => prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)));
      }
    } catch (err) {
      setError(getApiErrorMessage(err, 'Не удалось переключить статус'));
    } finally {
      setTogglingId(null);
    }
  };

  const handlePrint = async (row, barcode, e) => {
    e?.stopPropagation?.();
    const key = `${row.id}:${barcode}`;
    setPrintingKey(key);
    setError('');
    try {
      const res = await api.printBarcodeMismatchLabel(secret, row.id, barcode);
      const file = res?.data?.file;
      const filename = res?.data?.filename || `label-${barcode || row.wh_product_id}.pdf`;
      if (!file) throw new Error('WH не вернул файл этикетки');
      downloadBase64Pdf(file, filename);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Не удалось сгенерировать этикетку'));
    } finally {
      setPrintingKey(null);
    }
  };

  const unverified = items.filter((r) => !r.is_verified).length;

  return (
    <div className="bcmr-page">
      <Container fluid className="py-3">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
          <div>
            <h1 className="h4 mb-1">Баркоды на проверку (WH mismatch)</h1>
            <div className="text-muted small">
              Клик по строке — ячейки WH. Исправление: списание → приёмка → remove/place в ячейке.
            </div>
          </div>
          <div className="d-flex align-items-center gap-2">
            <Badge bg="warning" text="dark">
              Не проверено: {unverified}
            </Badge>
            <Badge bg="secondary">Всего: {items.length}</Badge>
            <Button variant="outline-secondary" size="sm" onClick={load} disabled={loading}>
              Обновить
            </Button>
          </div>
        </div>

        {error ? <Alert variant="danger">{error}</Alert> : null}

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" />
          </div>
        ) : (
          <div className="table-responsive bcmr-table-wrap">
            <Table bordered hover size="sm" className="bcmr-table align-middle mb-0">
              <thead>
                <tr>
                  <th style={{ minWidth: 160 }}>Баркоды на проверку</th>
                  <th>Варианты баркодов</th>
                  <th style={{ width: 140 }}>Статус</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="text-center text-muted py-4">
                      Нет данных. Запустите фоновую задачу refresh_barcode_mismatch_review.
                    </td>
                  </tr>
                ) : (
                  items.map((row) => {
                    const isExpanded = expandedId === row.id;
                    return (
                      <React.Fragment key={row.id}>
                        <tr
                          className={[
                            row.is_verified ? 'bcmr-row--verified' : 'bcmr-row--pending',
                            isExpanded ? 'bcmr-row--expanded' : '',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleRowClick(row)}
                        >
                          <td>
                            <div className="fw-semibold font-monospace">
                              {row.problem_barcode_display || row.problem_barcode || '(пусто)'}
                            </div>
                            <div className="small text-muted mt-1">
                              {row.mismatch_status}
                              {row.stock_sum != null ? ` · stock ${row.stock_sum}` : ''}
                              {isExpanded ? ' · ▼' : ' · ▶'}
                            </div>
                            {row.wh_product_name ? (
                              <div
                                className="small text-muted text-truncate"
                                title={row.wh_product_name}
                              >
                                {row.wh_product_name}
                              </div>
                            ) : null}
                            {row.platform_order_nos?.length ? (
                              <div className="small text-muted">
                                WB: {row.platform_order_nos.slice(0, 3).join(', ')}
                                {row.platform_order_nos.length > 3
                                  ? ` +${row.platform_order_nos.length - 3}`
                                  : ''}
                              </div>
                            ) : null}
                          </td>
                          <td>
                            {(row.variant_options || []).length === 0 ? (
                              <span className="text-muted">
                                Нет вариантов среди связанных OrderItem
                              </span>
                            ) : (
                              <ul className="list-unstyled mb-0 bcmr-variants">
                                {row.variant_options.map((opt) => {
                                  const pkey = `${row.id}:${opt.barcode}`;
                                  return (
                                    <li key={opt.barcode} className="bcmr-variant-row">
                                      <code className="me-2">{opt.barcode}</code>
                                      <VariantTraits color={opt.color} size={opt.size} />
                                      <Button
                                        variant="outline-primary"
                                        size="sm"
                                        className="ms-2 text-nowrap"
                                        disabled={printingKey === pkey}
                                        onClick={(e) => handlePrint(row, opt.barcode, e)}
                                      >
                                        {printingKey === pkey ? (
                                          <>
                                            <Spinner
                                              animation="border"
                                              size="sm"
                                              className="me-1"
                                            />
                                            …
                                          </>
                                        ) : (
                                          'Печать ШК'
                                        )}
                                      </Button>
                                    </li>
                                  );
                                })}
                              </ul>
                            )}
                          </td>
                          <td>
                            <Button
                              variant={row.is_verified ? 'success' : 'outline-warning'}
                              size="sm"
                              className="w-100"
                              disabled={togglingId === row.id}
                              onClick={(e) => handleToggle(row, e)}
                            >
                              {togglingId === row.id ? (
                                <Spinner animation="border" size="sm" />
                              ) : row.is_verified ? (
                                'Проверено'
                              ) : (
                                'НЕ проверено'
                              )}
                            </Button>
                          </td>
                        </tr>
                        {isExpanded ? (
                          <tr className="bcmr-expand-tr">
                            <td colSpan={3} className="p-0">
                              <ExpandedRowPanel
                                secret={secret}
                                row={row}
                                containersState={containersById[row.id]}
                                onReloadContainers={() => loadContainers(row.id)}
                              />
                            </td>
                          </tr>
                        ) : null}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>
        )}
      </Container>

      <style>{`
        .bcmr-page { min-height: 100vh; background: var(--bs-body-bg); }
        .bcmr-table-wrap { max-height: calc(100vh - 120px); overflow: auto; }
        .bcmr-table thead th {
          position: sticky; top: 0; z-index: 1;
          background: var(--bs-body-bg);
        }
        .bcmr-row--verified { opacity: 0.72; }
        .bcmr-row--expanded { background: rgba(13, 110, 253, 0.06); }
        .bcmr-expand {
          background: rgba(0, 0, 0, 0.03);
          border-top: 1px solid var(--bs-border-color);
        }
        .bcmr-variant-row {
          display: flex; flex-wrap: wrap; align-items: center;
          gap: 0.35rem; padding: 0.35rem 0;
          border-bottom: 1px solid var(--bs-border-color);
        }
        .bcmr-variant-row:last-child { border-bottom: 0; }
        .bcmr-trait {
          padding: 0.05rem 0.35rem;
          border-radius: 0.25rem;
          font-weight: 600;
        }
        .bcmr-trait--color {
          background: #fff3cd;
          color: #664d03;
        }
        .bcmr-trait--size {
          background: #cfe2ff;
          color: #084298;
        }
      `}</style>
    </div>
  );
}
